import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
  Box,
  Divider
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { EvidenceAccordion } from '../common/EvidenceAccordion';
import { ConfirmDialog } from '../common/ConfirmDialog';

/**
 * DraftReviewCard Component
 * Human-in-the-loop draft approval interface
 */

interface EvidenceRef {
  refType: "CGM_EVENT" | "MEAL_ENTRY" | "CONTEXT_NOTE" 
         | "LAB_RESULT" | "PROTOCOL_VERSION" | "MESSAGE_RESPONSE";
  refId: string;
  label?: string;
  meta?: Record<string, unknown>;
}

interface MessageDraft {
  id: string;
  patientId: string;
  patientName: string;
  draftContent: string;
  rationale?: string;
  evidenceRefs: EvidenceRef[];
  status: "PENDING_REVIEW" | "APPROVED" | "REJECTED";
  createdAt: string;
}

type DraftDecision = "APPROVE" | "REJECT" | "EDIT_AND_APPROVE";

interface DraftReviewCardProps {
  draft: MessageDraft;
  onApprove: (decision: DraftDecision, editedContent?: string) => Promise<void>;
  onClose: () => void;
}

export const DraftReviewCard: React.FC<DraftReviewCardProps> = ({
  draft,
  onApprove,
  onClose
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(draft.draftContent);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<DraftDecision | null>(null);
  const [loading, setLoading] = useState(false);

  const handleDecision = async (decision: DraftDecision) => {
    setLoading(true);
    try {
      const content = decision === "EDIT_AND_APPROVE" ? editedContent : undefined;
      await onApprove(decision, content);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = (decision: DraftDecision) => {
    setPendingDecision(decision);
    setConfirmOpen(true);
  };

  return (
    <>
      <Card elevation={3}>
        <CardHeader
          title="AI Draft for Review"
          subheader={`Patient: ${draft.patientName}`}
        />

        <CardContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Human-in-the-Loop Required:</strong> This AI-generated message 
            will only be sent to the patient after your approval.
          </Alert>

          {draft.rationale && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                AI Rationale:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {draft.rationale}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Message Content:
          </Typography>

          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={6}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              variant="outlined"
            />
          ) : (
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: 1,
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {draft.draftContent}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <EvidenceAccordion
              evidenceRefs={draft.evidenceRefs}
              defaultExpanded
            />
          </Box>
        </CardContent>

        <CardActions>
          <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => handleConfirm("REJECT")}
              disabled={loading}
            >
              Reject
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {!editMode ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                    disabled={loading}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleConfirm("APPROVE")}
                    disabled={loading}
                  >
                    Approve as-is
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      setEditMode(false);
                      setEditedContent(draft.draftContent);
                    }}
                    disabled={loading}
                  >
                    Cancel Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleConfirm("EDIT_AND_APPROVE")}
                    disabled={loading}
                  >
                    Approve Edited Version
                  </Button>
                </>
              )}
            </Box>
          </Stack>
        </CardActions>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Decision"
        message={
          pendingDecision === "REJECT"
            ? "Are you sure you want to reject this draft? It will not be sent to the patient."
            : "After approval, this message will be sent to the patient. Continue?"
        }
        severity={pendingDecision === "REJECT" ? "warning" : "info"}
        confirmLabel="Confirm"
        onConfirm={() => {
          setConfirmOpen(false);
          if (pendingDecision) handleDecision(pendingDecision);
        }}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDecision(null);
        }}
      />
    </>
  );
};
